import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Información que recopilamos',
    body: 'Recopilamos únicamente la información necesaria para procesar tu compra: nombre, correo electrónico y datos de pago. No almacenamos datos de tarjetas de crédito directamente; estos son procesados de forma segura por nuestro proveedor de pagos.'
  }, {
    heading: 'Uso de la información',
    body: 'Tu información se utiliza exclusivamente para: entregar el producto digital adquirido, enviar comunicaciones relacionadas con tu compra y mejorar nuestros servicios. No vendemos, alquilamos ni compartimos tu información personal con terceros.'
  }, {
    heading: 'Cookies',
    body: 'Utilizamos cookies esenciales para el funcionamiento del sitio y cookies analíticas para entender cómo los visitantes interactúan con nuestra página. Puedes desactivar las cookies en la configuración de tu navegador.'
  }, {
    heading: 'Seguridad',
    body: 'Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, pérdida o alteración.'
  }, {
    heading: 'Tus derechos',
    body: 'Tienes derecho a acceder, rectificar o eliminar tus datos personales en cualquier momento. Para ejercer estos derechos, puedes contactarnos a través de los canales indicados en tu correo de confirmación de compra.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: 'Producto digital',
    body: '"Apaga la Ansiedad Nocturna" es un producto digital (PDF con plantillas imprimibles) de descarga inmediata. Al completar la compra, recibirás acceso instantáneo al material.'
  }, {
    heading: 'Uso personal',
    body: 'El contenido adquirido es para uso personal y no comercial. Queda prohibida la reproducción, distribución o reventa del material sin autorización expresa.'
  }, {
    heading: 'Descargo de responsabilidad',
    body: 'Este producto es de carácter educativo e informativo. No constituye consejo médico, psicológico ni terapéutico. No sustituye el diagnóstico ni tratamiento profesional. Si experimentas síntomas severos de ansiedad, consulta a un profesional de salud.'
  }, {
    heading: 'Política de reembolso',
    body: 'Ofrecemos una garantía de satisfacción de 14 días. Si el producto no cumple tus expectativas, puedes solicitar un reembolso completo dentro de los 14 días posteriores a la compra.'
  }, {
    heading: 'Modificaciones',
    body: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigor desde su publicación en esta página.'
  }]
};
export function FooterLegal() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  useEffect(() => {
    if (activeModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [activeModal]);
  const content = activeModal === 'privacy' ? privacyContent : termsContent;
  return <>
      <footer className="py-12 px-6 text-center border-t border-white/[0.04]" data-id="element-5">
        <p className="text-[#8b9bb4]/40 text-sm" data-id="element-6">
          © {new Date().getFullYear()} Apaga la Ansiedad Nocturna. Todos los
          derechos reservados.
        </p>
        <p className="mt-2 text-[#8b9bb4]/30 text-xs" data-id="element-7">
          Este producto no sustituye el consejo médico profesional.
        </p>
        <div className="mt-4 flex items-center justify-center gap-4" data-id="element-8">
          <button onClick={() => setActiveModal('privacy')} className="text-[#8b9bb4]/50 text-xs hover:text-[#c9956b] transition-colors cursor-pointer" data-id="element-9">
            Política de Privacidad
          </button>
          <span className="text-[#8b9bb4]/20 text-xs" data-id="element-10">·</span>
          <button onClick={() => setActiveModal('terms')} className="text-[#8b9bb4]/50 text-xs hover:text-[#c9956b] transition-colors cursor-pointer" data-id="element-11">
            Términos y Condiciones
          </button>
        </div>
      </footer>

      <AnimatePresence data-id="element-12">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-13">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-14" />

            {/* Modal */}
            <motion.div initial={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 10,
          scale: 0.97
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} onClick={e => e.stopPropagation()} className="relative z-10 bg-[#111827] border border-white/[0.08] rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col" data-id="element-15">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/[0.06]" data-id="element-16">
                <h3 className="text-lg font-bold text-[#f5efe3] font-serif" data-id="element-17">
                  {content.title}
                </h3>
                <button onClick={() => setActiveModal(null)} className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors cursor-pointer" data-id="element-18">
                  <X className="w-4 h-4 text-[#8b9bb4]" data-id="element-19" />
                </button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto px-6 py-6 space-y-6 flex-1" data-id="element-20">
                {content.sections.map((section, i) => <div key={i} data-id="element-21">
                    <h4 className="text-sm font-semibold text-[#c9956b] uppercase tracking-wider mb-2" data-id="element-22">
                      {section.heading}
                    </h4>
                    <p className="text-[#8b9bb4] text-sm leading-relaxed" data-id="element-23">
                      {section.body}
                    </p>
                  </div>)}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-white/[0.06]" data-id="element-24">
                <button onClick={() => setActiveModal(null)} className="w-full py-2.5 rounded-lg bg-white/5 text-[#f0e6d3] text-sm font-medium hover:bg-white/10 transition-colors cursor-pointer" data-id="element-25">
                  Cerrar
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}